import React from 'react';

import ApplicationContext from '../contexts/application';
import { config } from '../libs/config';
import { CustomDataGrid } from '../components/CustomDataGrid';
import { Box, Button, Checkbox, Container, Divider, Grid, LinearProgress, Menu, MenuItem, Typography } from '@mui/material';
import { useLocation, useNavigate } from "react-router";
import CustomFilter from '../components/CustomFilter';
import { getAlarmTypeGroup, saveAlarmTypeGroup } from '../libs/apiCalls';

function AlarmGroupTypes() {
    const defaultPageSize = 50;
    const navigate = useNavigate();

    const { settings, setDialog, setSnackbar } = React.useContext(ApplicationContext);
    const { state: source } = useLocation();

    const [loading, setLoading] = React.useState(false);
    const [alarmRows, setAlarmRows] = React.useState([]);

    const [contextMenu, setContextMenu] = React.useState(null);
    
    const filter = React.useRef({
        searchString: '',
        fromDate: '',
        toDate: '',
        priority: '',
        topic: '',
        status: '',
        source: '',
        roles: settings.roles
    });

    const alarmColumns = [
        { field: 'alarmTypeId', headerName: 'ID' },
        { field: 'source', headerName: 'Source', flex: 0.1 },
        { field: 'alarmType', headerName: 'Alarm Type', flex: 0.5 },
        { field: 'priority', headerName: 'Priority', flex: 0.2 },
        {
            field: 'route02', headerName: 'Alarm Viewer', flex: 0.1, align: 'center',
            renderCell: (props) => (
                <Checkbox
                    name={props.field}
                    checked={props.value !== null}
                    value='esb.twx-alarms'
                    onChange={handleCheckboxChange(props.row.id)}
                />
            )
        },
        {
            field: 'route03', headerName: 'Emails', flex: 0.1, align: 'center',
            renderCell: (props) => (
                <Checkbox
                    name={props.field}
                    checked={props.value !== null}
                    value='esb.email-out'
                    onChange={handleCheckboxChange(props.row.id)}
                />
            )
        },
        {
            // Per-group half of the push gate. Only has an effect where the alarm type itself
            // has Push Notification ticked - this decides which of that type's groups it
            // reaches, and a group with no UserRole is skipped because a device is registered
            // against a role.
            field: 'route04', headerName: 'Push', flex: 0.1, align: 'center',
            renderCell: (props) => (
                <Checkbox
                    name={props.field}
                    checked={props.value !== null}
                    value='esb.push-out'
                    onChange={handleCheckboxChange(props.row.id)}
                />
            )
        }
    ];

    const getData = (filter, page, pageSize) => {
        filter.roles = settings.roles;
        setLoading(true);

        getAlarmTypeGroup(settings.token, JSON.stringify(filter), source.id,
            (response) => {
                if (!response.data.error) {
                    setAlarmRows(JSON.parse(response.data.result));
                    setLoading(false);
                }
                else {
                    setDialog(response.data.errors[0], true);
                    setLoading(false);
                }
            },
            (error) => {
                setDialog(error.message, true);
            }
        )
    };

    const handleFilter = () => {
        getData(filter.current, 0, defaultPageSize);
    };

    const handleClearFilter = () => {
        getData(filter.current, 0, defaultPageSize);
    };


    const handleCheckboxChange = (id) => (event) => {
        setAlarmRows((prevRows) => {
            return prevRows.map((row) => row.id === id ? 
                { ...row, 
                    [event.target.name]: event.target.checked ? event.target.value : null,
                    updatedBy: settings.userName
                } 
                : row);
        });
    };

    const handleRefresh = () => {
    };

    const handleSave = () => {
        setLoading(true);

        saveAlarmTypeGroup(settings.token, JSON.stringify(alarmRows),
            (response) => {
                if (!response.data.error) {
                    setSnackbar("Record added/updated successfully.");
                }
                else {
                    setDialog(response.data.errors[0], true);
                }

                setLoading(false);
            },
            (error) => {
                setDialog(error.message, true);
                setLoading(false);
            }
        )
    }

    const handleClearAllClick = (event) => {
        setContextMenu(null);

        console.log(event.field);
    };

    const handleContextMenu = (event) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
                : null,
        );
    };

    React.useEffect(() => {
        if (Object.keys(settings).length > 0) {
            getData(filter.current, 0, defaultPageSize);
        }
    }, [settings]);

    // This screen is the 'group' half of alarm routing and exists in no other mode. Guarded
    // here as well as at its route and its one entry point, so no path that outlives those
    // two lands somebody on a screen their console does not otherwise offer.
    if (config.alarmRouting !== 'group') {
        return null;
    }

    return (
        <>
            <Box sx={{ display: 'flex', mb: 2 }}>
                <Box sx={{ width: "50%" }}>
                    <Typography sx={{ marginLeft: "5px", fontWeight: '400', color: 'text.bright' }} style={{ fontSize: "25px" }}>Alarm Groups - {source.description}</Typography>
                    <Typography sx={{ marginLeft: "5px", fontWeight: '300', color: 'text.secondary' }} style={{ fontSize: "15px" }}>Configure alarm routing for the selected group.</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: "50%", mb: 4 }}>
                    <Button variant="outlined" color="error"
                        style={{ width: "120px" }}
                        onClick={() => navigate('/admin/alarmGroups')}
                    >
                        Back
                    </Button>
                    <Button variant="outlined" style={{ width: "120px", marginLeft: "7px" }} onClick={handleRefresh}>Refresh</Button>
                    <Button variant="contained" color="success" style={{ width: "120px", marginLeft: "7px" }} onClick={handleSave} >Save</Button>
                </Box>
            </Box>
            <Divider sx={{ mb: 4 }} />
            <Container maxWidth="xl">
                <Grid container gap={2} sx={{ mt: 2 }}>
                    <Grid item gap={2} sx={{ display: 'flex', width: '100%' }}>
                        <CustomFilter filter={filter}
                            showDates={false}
                            onFilter={handleFilter}
                            onClearFilter={handleClearFilter}
                        />
                    </Grid>

                    <Box sx={{ height: '100%', width: '100%' }}>
                        <LinearProgress sx={{ display: loading ? 'display' : 'none' }} />
                        <CustomDataGrid
                            columns={alarmColumns}
                            rows={alarmRows}

                            getRowId={(row) => row.id}

                            pagination
                            pageSizeOptions={[10, 20, 30, 50]}

                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: defaultPageSize,
                                    },
                                },
                            }}
                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                            }

                            autoHeight
                        />
                    </Box>
                </Grid>
                <Menu
                    open={contextMenu !== null}
                    onClose={() => setContextMenu(null)}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextMenu !== null
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                >
                    <MenuItem onClick={handleClearAllClick}>
                        Clear
                    </MenuItem>
                </Menu>
            </Container>
        </>
    );
}

export default AlarmGroupTypes;